/**
 * API client. Every call attaches the Firebase ID token; the server verifies
 * it and checks for an `orgId` claim on every route without exception.
 * SPEC-0003 §3.
 */
import type {
	AgentChatRequest,
	AgentChatResponse,
	CreateRunRequest,
	ExtractSoldDocumentResponse,
	MappingProfile,
	Project,
	RunDetail,
	RunListItem,
	StoredSeverityConfig,
	UpdateProjectRequest,
} from '@picklii/shared';

export class ApiError extends Error {
	constructor(
		readonly status: number,
		readonly code: string,
		message: string,
		readonly details?: unknown,
	) {
		super(message);
		this.name = 'ApiError';
	}

	/** 401: sign in. 403: stop and explain — never a sign-in loop. §4. */
	get isUnauthenticated(): boolean {
		return this.status === 401;
	}

	get isForbidden(): boolean {
		return this.status === 403;
	}
}

type TokenProvider = () => Promise<string | null>;

let getToken: TokenProvider = async () => null;

export function setTokenProvider(provider: TokenProvider): void {
	getToken = provider;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
	const token = await getToken();
	const headers = new Headers(init.headers);
	if (token !== null) headers.set('Authorization', `Bearer ${token}`);

	const response = await fetch(`/api${path}`, { ...init, headers });

	if (!response.ok) {
		const body = (await response.json().catch(() => null)) as
			| { error?: { code?: string; message?: string; details?: unknown } }
			| null;
		throw new ApiError(
			response.status,
			body?.error?.code ?? 'internal',
			body?.error?.message ?? response.statusText,
			body?.error?.details,
		);
	}

	// 204 carries no body; parsing it as JSON would throw on a successful delete.
	if (response.status === 204) return undefined as T;
	return (await response.json()) as T;
}

export const api = {
	/** Reaching this without an ApiError means the account is authorized. */
	me: () => request<{ uid: string; email: string }>('/me'),
	listRuns: () => request<{ runs: RunListItem[] }>('/runs').then((r) => r.runs),
	getRun: (id: string) => request<RunDetail>(`/runs/${encodeURIComponent(id)}`),

	deleteRun: (id: string) =>
		request<void>(`/runs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
	listProfiles: () => request<{ profiles: MappingProfile[] }>('/profiles').then((r) => r.profiles),

	/** SPEC-0007. The Dashboard's project list, active and archived alike —
	 * the caller splits by `archived` rather than this issuing two calls. */
	listProjects: () => request<{ projects: Project[] }>('/projects').then((r) => r.projects),

	updateProject: (projectId: string, body: UpdateProjectRequest) =>
		request<Project>(`/projects/${encodeURIComponent(projectId)}`, {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		}),

	getSeverityConfig: () => request<StoredSeverityConfig>('/settings/severity'),

	saveSeverityConfig: (config: StoredSeverityConfig) =>
		request<StoredSeverityConfig>('/settings/severity', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(config),
		}),

	getProfile: (id: string) => request<MappingProfile>(`/profiles/${encodeURIComponent(id)}`),

	updateProfile: (id: string, profile: MappingProfile) =>
		request<MappingProfile>(`/profiles/${encodeURIComponent(id)}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(profile),
		}),

	deleteProfile: (id: string) =>
		request<void>(`/profiles/${encodeURIComponent(id)}`, { method: 'DELETE' }),

	saveProfile: (profile: Omit<MappingProfile, 'id' | 'updatedAt'>) =>
		request<MappingProfile>('/profiles', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(profile),
		}),

	/**
	 * One agent turn. SPEC-0004 §2.1.
	 *
	 * The whole conversation goes up every time — the server keeps no session
	 * state, so the tab that owns the history is the only thing that has it.
	 */
	agentChat: (body: AgentChatRequest) =>
		request<AgentChatResponse>('/agent/chat', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		}),

	/**
	 * The files and the confirmed mapping post together, and the server
	 * re-parses them itself. SPEC-0001 step 4.
	 *
	 * `sold` is a list, not a single file: a change order sometimes arrives as
	 * its own export rather than a second line in the original one (issue
	 * #30). Appending the same form field multiple times is what produces
	 * multiple `sold` parts server-side — `runs.ts` merges them before
	 * reconciling.
	 */
	createRun(files: { sold: File[]; budget: File }, body: CreateRunRequest) {
		const form = new FormData();
		form.append('request', JSON.stringify(body));
		for (const file of files.sold) form.append('sold', file);
		form.append('budget', files.budget);
		return request<RunDetail>('/runs', { method: 'POST', body: form });
	},

	/**
	 * SPEC-0006 (Draft) — NOT approved for production customer use; see that
	 * spec's open questions 1 and 5. One document per call; the caller merges
	 * results across documents the way the sold side already merges CSVs
	 * (SPEC-0001 §1).
	 */
	extractSoldDocument(file: File) {
		const form = new FormData();
		form.append('document', file);
		return request<ExtractSoldDocumentResponse>('/runs/extract-sold-document', {
			method: 'POST',
			body: form,
		});
	},
};
